import Vue from 'vue';
import objectHash from 'object-hash';
import Vuex from 'vuex';
import gql from 'graphql-tag';
import {getProducts} from '@breakmedia/prime-vue-product/src/store/getProducts';
import {Observable} from 'rxjs';
import {first} from 'rxjs/operators';

Vue.use(Vuex);

Vue.localStorage.basket = {
    type: Object,
    default: {}
};

export default new Vuex.Store({
    namespaced: true,
    state() {
        let items = {};
        let options = {};
        let basket = {};
        try {
            basket = JSON.parse(Vue.localStorage.get('basket'));
            if (basket && typeof basket == 'object') {
                if (basket.items) {
                    items = basket.items;
                }
                if (basket.options) {
                    options = basket.options;

                    //Temporary solution to handle old single coupons stored locally.
                    if (options && options.coupon && !Array.isArray(options.coupon)) {
                        if (typeof options.coupon === 'object' && options.coupon.value) {
                            options.coupon = [options.coupon.value];
                        } else if (typeof options !== 'object') {
                            options.coupon = [options.coupon];
                        }
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }
        if (!basket || typeof basket != 'object') {
            basket = {};
        }
        if (!items || typeof items != 'object') {
            items = {};
        }
        if (!options || typeof options != 'object') {
            options = {};
        }

        basket.items = items;
        basket.options = options;
        const coupon = basket.options.hasOwnProperty('coupon') ? basket.options.coupon : null;
        for (const hash of Object.keys(basket.items)) {
            const basketItem = basket.items[hash];
            if (basketItem.options.hasOwnProperty('boundCoupon') && coupon.includes(basketItem.options.boundCoupon)) {
                delete basket.items[hash];
            }
        }
        Vue.localStorage.set('basket', JSON.stringify(basket));
        return {
            items: items,
            lastBasketChange: null,
            actualPricesLoaded: false,
            basketOptions: options,
            basket: basket,
            serverBasket: {},
            shippingPrices: {},
            discounts: {},
            countryId: null,
            basketId: basket._id ? basket._id : null,
            latestAddedItems: {},
            saveBasketAbortController: null,
            basketTotal: 0,
            basketConfig: {},
        };
    },
    getters: {
        isBasketSyncAllowed: () => {
            return Vue.$store.state.Login && Vue.$store.getters['Login/isUserLoggedIn'];
        },
        getConvertedLocalBasket: state => localBasket => {
            if (!localBasket) {
                localBasket = state.basket;
            }

            const convertedBasket = Object.assign({}, localBasket);
            const basketItems = {};
            const basketOptions = {};
            Object.keys(convertedBasket.items).forEach(id => {
                const item = Object.assign({}, convertedBasket.items[id]);
                item.master_catalog_item_id = item.product_id;
                item.is_pickup = item.pickup;
                basketItems[item.hash] = item;
            });
            Object.keys(convertedBasket.options).forEach(key => {
                const option = convertedBasket.options[key];
                if (typeof option === 'object' && option.key && option.value) {
                    basketOptions[option.key] = option.value;
                } else {
                    basketOptions[key] = option;
                }
            });

            convertedBasket.items = basketItems;
            convertedBasket.options = basketOptions;

            return convertedBasket;
        },
        getConvertedServerBasket: state => serverBasket => {
            if (!serverBasket) {
                serverBasket = state.serverBasket;
                if (serverBasket.items) {
                    serverBasket.items = Object.values(serverBasket.items);
                } else {
                    serverBasket.items = [];
                }
            }

            const basketItems = [];
            if (serverBasket && serverBasket.items) {
                serverBasket.items.forEach(serverItem => {
                    basketItems[serverItem.hash] = serverItem;
                });
            }
            serverBasket.items = basketItems;

            return serverBasket;
        },
        getMergedBasket: (state, getters) => (serverBasket, forceServerBasket = true, forceLocalBasket = false) => {
            let localBasket = Object.assign({}, state.basket);
            let localBasketDuplicate = Object.assign({}, localBasket);

            if (!serverBasket) {
                serverBasket = state.serverBasket;
                if (serverBasket.items) {
                    serverBasket.items = Object.values(serverBasket.items);
                } else {
                    serverBasket.items = [];
                }
            }

            let convertedServerBasket = getters.getConvertedServerBasket(serverBasket);

            if (!localBasket.items) {
                localBasket.items = {};
            }

            let newItems = {};
            Object.values(convertedServerBasket.items).forEach(serverItem => {
                const hash = getters.getItemHash(serverItem);
                serverItem.hash = hash;

                if (forceLocalBasket && localBasket.items[hash]) {
                    serverItem.quantity = localBasket.items[hash].quantity;
                } else if (!forceServerBasket && localBasket.items[hash]) {
                    serverItem.quantity = Math.max(localBasket.items[hash].quantity, parseInt(serverItem.quantity));
                } else {
                    serverItem.quantity = parseInt(serverItem.quantity);
                }

                newItems[hash] = serverItem;
            });

            if (forceServerBasket) {
                //remove local items which does not exist in server data;
                Object.keys(localBasketDuplicate.items).forEach(localHash => {
                    let found = false;
                    for (const newHash of Object.keys(newItems)) {
                        if (localHash == newHash) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        Vue.delete(localBasket.items, localHash);
                    }
                });
            }

            Object.keys(newItems).forEach(newHash => {
                Vue.set(localBasket.items, newHash, newItems[newHash]);
            });

            if (convertedServerBasket.options && Object.values(convertedServerBasket.options).length) {
                Object.keys(convertedServerBasket.options).forEach(name => {
                    if (!localBasket.options[name]) {
                        Vue.set(localBasket.options, name, convertedServerBasket.options[name]);
                    }
                });
            }
            return localBasket;
        },
        getItemHash: () => item => {
            return objectHash.MD5({
                catalog_item_id: parseInt(item.catalog_item_id),
                options: item.options,
                buyOptions: item.buyOptions,
            });
        },
    },
    mutations: {
        addItem(state, value) {
            value.quantity = parseInt(value.quantity);
            //@todo: We need a typescript basketItem class to strictly structure it and ignore extra fields.
            delete value.product;
            if (value.hash) {
                if (state.items[value.hash]) {
                    state.items[value.hash].quantity += value.quantity;
                } else {
                    Vue.set(state.items, value.hash, value);
                }
                state.basket.items = state.items;
            }
        },
        updateItem(state, value) {
            delete value.product;
            if (value.hash && state.items[value.hash]) {
                if (value.quantity) {
                    state.items[value.hash].quantity = parseInt(value.quantity);
                }
                if (value.pickup != undefined) {
                    state.items[value.hash].pickup = Boolean(value.pickup);
                }
                state.basket.items = state.items;
            }
        },
        removeItem(state, value) {
            delete value.product;
            if (value.hash && state.items[value.hash]) {
                Vue.delete(state.items, value.hash);
                Vue.delete(state.latestAddedItems, value.hash);
                state.basket.items = state.items;
            }
        },
        basketChanged(state) {
            state.actualPricesLoaded = false;
            state.lastBasketChange = Date.now();
        },
        setActualPricesLoaded(state) {
            state.actualPricesLoaded = true;
        },
        addBasketOption: (state, option) => {
            if (option.key && option.value) {
                Vue.set(state.basketOptions, option.key, option.value);
            } else if (option.key) {
                Vue.delete(state.basketOptions, option.key);
            }
            Vue.set(state.basket, 'options', state.basketOptions);
        },
        removeBasketOption: (state, key) => {
            Vue.delete(state.basketOptions, key);
            state.basket.options = state.basketOptions;
        },
        setServerBasket: (state, serverBasket) => {
            state.serverBasket = serverBasket;
            if (serverBasket && serverBasket._id) {
                state.basketId = serverBasket._id;
            }
        },
        setShippingPrices(state, value) {
            state.shippingPrices = value;
        },
        setCountryId(state, value) {
            state.countryId = value;
        },
        addDiscount: (state, options) => {
            if (options.key && options.value) {
                Vue.set(state.discounts, options.key, options.value);
            } else if (options.key && state.discounts[options.key]) {
                Vue.delete(state.discounts, options.key);
            }
        },
        setBasket: (state, basket) => {
            if (!basket.items) {
                basket.items = {};
            }
            if (!basket.options) {
                basket.options = {};
            }

            if (state.basketConfig.allowStorage) {
                Vue.localStorage.set('basket', JSON.stringify(basket));
            }

            Vue.set(state, 'items', basket.items);
            Vue.set(state, 'options', basket.options);

            state.basket = basket;
            state.actualPricesLoaded = false;
            state.lastBasketChange = Date.now();
        },
        setBasketId: (state, basketId) => {
            state.basketId = basketId;
        },
        resetLatestAddedItems: state => {
            state.latestAddedItems = {};
        },
        addLatestAddedItems: (state, value) => {
            const basketItem = Object.assign({}, value);
            if (basketItem && basketItem.hash) {
                if (basketItem.keepProduct) {
                    delete basketItem.keepProduct;
                } else {
                    const product = Vue.$basket.getBasketItemProduct(basketItem);
                    basketItem.product = product;
                }
                Vue.set(state.latestAddedItems, basketItem.hash, basketItem);
            }
        },
        setSaveBasketAbortController(state, value) {
            state.saveBasketAbortController = value;
        },
        setBasketTotal(state, value) {
            state.basketTotal = value;
        },
        setBasketConfig (state, basketConfig) {
            state.basketConfig = basketConfig;
            if (!basketConfig.allowStorage) {
                Vue.localStorage.set('basket', JSON.stringify({}));
            }
        },
    },
    actions: {
        addBasketItem({ commit, dispatch, getters }, value) {
            let silent = false;
            if (value.hasOwnProperty('silent')) {
                silent = value.silent;
                delete value.silent;
            }

            let keepProduct = false;
            if (value.hasOwnProperty('keepProduct')) {
                keepProduct = value.keepProduct;
                delete value.keepProduct;
            }

            value.hash = getters.getItemHash(value);
            commit('addLatestAddedItems', { ...value, keepProduct });
            commit('addItem', value);

            if (silent === false) {
                dispatch('saveBasket');
            }

            return Object.assign({}, value);
        },
        syncBasketToServer({ dispatch, getters, commit, state }, { forceMerge = true }) {
            return Vue.$graphql
                .query({
                    query: gql`
                        query getCustomerBasket {
                            getCustomerBasket {
                                _id
                                items {
                                    catalog_item_id
                                    product_id
                                    hash
                                    quantity
                                    options
                                    attributes
                                    price
                                    rrp_price
                                    priceSignature
                                    buyOptions
                                }
                                options
                            }
                        }
                    `,
                    fetchPolicy: 'no-cache',
                })
                .then(r => r.data.getCustomerBasket)
                .then(result => {
                    commit('setBasketId', result._id ? result._id : null);

                    //Temporary solution to handle single coupons stored locally.
                    if (result.options && result.options.coupon && !Array.isArray(result.options.coupon)) {
                        const options = result.options;
                        if (typeof options.coupon === 'object' && options.coupon.value) {
                            options.coupon = [options.coupon.value];
                        } else if (typeof options !== 'object') {
                            options.coupon = [options.coupon];
                        }
                    }

                    if (
                        result.items &&
                        result.items.length > 0 &&
                        Object.values(state.items).length > 0
                    ) {
                        const extraItems = [];
                        result.items.forEach(serverItem => {
                            if (
                                !state.items.hasOwnProperty(serverItem.hash) ||
                                state.items[serverItem.hash].catalog_item_id !== serverItem.catalog_item_id
                            ) {
                                extraItems.push(serverItem);
                            }
                        });

                        if (extraItems.length === 0) {
                            //nothing different - we can auto merge baskets.
                            return dispatch('saveBasket', {
                                basket: getters.getMergedBasket(result, forceMerge),
                            });
                        } else {
                            //there are different items on the server. Will ask customer to add/remove them.
                            commit('setServerBasket', result);
                        }
                    } else if (!result.items.length) {
                        //server basket is empty. Save local.
                        return dispatch('saveBasket');
                    } else {
                        //It's not a login sync - rewrite local basket with server's.
                        return dispatch('saveBasket', {
                            basket: getters.getMergedBasket(result),
                            saveToServer: false,
                        });
                    }
                })
                .catch(err => {
                    if (
                        err.graphQLErrors &&
                        err.graphQLErrors[0] &&
                        err.graphQLErrors[0].message &&
                        err.graphQLErrors[0].message.statusCode &&
                        err.graphQLErrors[0].message.statusCode === 404
                    ) {
                        //server basket is empty. Save local.
                        return dispatch('saveBasket');
                    }
                });
        },
        loadBasketProductsData() {
            return new Promise(resolve => {
                getProducts(new Observable(subscriber => {
                    subscriber.next({
                        ids: Object.values(Vue.$basket.getBasketProductsIds()),
                        priceParams: Vue.$basket.getPriceParams(),
                    })
                })).pipe(first()).subscribe(products => {
                    Vue.$store.commit('Basket/setProducts', products);
                    Vue.$basket.applyPricesSignatures(products);
                    resolve(products);
                });
            });
        },
        updateBasketItem({ dispatch, commit }, item) {
            commit('updateItem', item);
            dispatch('saveBasket', { forceLocalBasket: true });
        },
        removeBasketItem({ dispatch, commit }, item) {
            commit('removeItem', item);
            dispatch('saveBasket', {
                forceSave: true,
            });

            return Object.assign({}, item);
        },
        removeBasketItemLocally({ dispatch, commit }, item) {
            commit('removeItem', item);
            dispatch('saveBasket', {
                forceSave: true,
                saveToServer: false,
            });

            return Object.assign({}, item);
        },
        addBasketOption({ dispatch, commit }, option) {
            commit('addBasketOption', option);
            return dispatch('saveBasket');
        },
        removeBasketOption({ dispatch, commit }, option) {
            commit('removeBasketOption', option);
            dispatch('saveBasket');
        },
        saveBasket({ state, getters, dispatch, commit }, options) {
            let basket;
            if (options && options.basket) {
                basket = options.basket;
            } else {
                basket = getters.getConvertedLocalBasket();
                if (!options || options.forceSave !== true) {
                    //ensure items which may be added in different tabs;
                    let localStorageBasket;
                    try {
                        localStorageBasket = JSON.parse(Vue.localStorage.get('basket'));
                    } catch (e) {}
                    if (localStorageBasket && localStorageBasket.items) {
                        Object.keys(localStorageBasket.items).forEach(hash => {
                            if (!basket.items[hash]) {
                                Vue.set(basket.items, hash, localStorageBasket.items[hash]);
                            }
                        });
                    }
                }
            }
            commit('setBasket', basket);
            if (Vue.$basket.isBasketSyncAllowed() && (!options || options.saveToServer !== false)) {
                const basketData = Object.assign({}, basket);
                const items = [];
                Object.values(basket.items).forEach(basketItem => {
                    items.push(basketItem);
                });
                const basketOptions = {};
                Object.keys(basket.options).forEach(basketOption => {
                    basketOptions[basketOption] = basket.options[basketOption];
                });
                basketData.items = items;
                basketData.options = basketOptions;
                basketData._id = state.basketId;

                if (state.saveBasketAbortController) {
                    state.saveBasketAbortController.abort();
                }

                commit('setSaveBasketAbortController', new AbortController());

                return Vue.$graphql
                    .mutate({
                        mutation: gql`
                            mutation($basket: BasketInput!) {
                                saveCustomerBasket(basket: $basket) {
                                    _id
                                    items {
                                        catalog_item_id
                                        product_id
                                        hash
                                        quantity
                                        options
                                        attributes
                                        price
                                        rrp_price
                                        priceSignature
                                        buyOptions
                                    }
                                    options
                                }
                            }
                        `,
                        variables: {
                            basket: basketData,
                        },
                        fetchPolicy: 'no-cache',
                        context: {
                            fetchOptions: {
                                signal: state.saveBasketAbortController.signal,
                            },
                        },
                    })
                    .then(r => r.data.saveCustomerBasket)
                    .then(result => {
                        commit('setSaveBasketAbortController', null);

                        const items = {};
                        for (const serverItem of Object.values(result.items)) {
                            items[serverItem.hash] = serverItem;
                        }
                        result.items = items;
                        commit('setBasketId', result._id);
                        commit('setBasket', result);
                    })
                    .catch(err => {
                        commit('setSaveBasketAbortController', null);

                        if (options && options.repeat) {
                            console.log(err.toString());
                        } else if (
                            err.graphQLErrors[0] &&
                            err.graphQLErrors[0].extensions &&
                            err.graphQLErrors[0].extensions.exception &&
                            err.graphQLErrors[0].extensions.exception.status === 422 &&
                            err.graphQLErrors[0].message
                        ) {
                            const newBasket = err.graphQLErrors[0].message;
                            commit('setBasketId', newBasket._id);
                            commit('setServerBasket', newBasket);
                            dispatch('saveBasket', {
                                basket: getters.getMergedBasket(
                                    newBasket,
                                    options ? !options.forceLocalBasket : true,
                                    options ? options.forceLocalBasket : false,
                                ),
                                repeat: true,
                            });
                        }
                    });
            }
        },
        deleteBasket({ commit }) {
            Vue.$graphql
                .query({
                    query: gql`
                        query deleteCustomerBasket {
                            deleteCustomerBasket {
                                _id
                                items {
                                    catalog_item_id
                                    product_id
                                    hash
                                    quantity
                                    options
                                    attributes
                                    price
                                    rrp_price
                                    priceSignature
                                    buyOptions
                                }
                                options
                            }
                        }
                    `,
                    fetchPolicy: 'no-cache',
                })
                .then(r => r.data.deleteCustomerBasket)
                .then(result => {
                    commit('setBasketId', result._id);
                    commit('setBasket', result);
                })
                .catch(e => {
                    commit('setBasketId');
                    commit('setBasket', {});
                });
        },
        recalculateBasketTotal({ commit, dispatch }, options = {}) {
            return new Promise(resolve => {
                if (options.loadMissingProducts) {
                    dispatch('loadBasketProductsData')
                        .then(() => {
                            commit('setBasketTotal', getTotal());
                            resolve();
                        })
                } else {
                    commit('setBasketTotal', getTotal());
                    resolve();
                }
            });
        },
    },
});

const getTotal = () => {
    const totalWithoutDiscount = Vue.$basket.getBasketTotalWithoutDiscount();
    const totalDiscount = Vue.$basket.getTotalDiscount();
    const total = totalWithoutDiscount - totalDiscount;

    return parseFloat((total).toFixed(2));
}
